/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { PageTitle } from '../../../rbts/layout/core';
import { toAbsoluteUrl } from '../../../rbts/helpers';
import '../../../rbts/assets/sass/gponce/dashboard.scss';
import { Modal } from 'react-bootstrap-v5';

const API = process.env.REACT_APP_API;

const IMG_FALLBACK = toAbsoluteUrl('/media/logos/no_image.png');

// ==== LOCKED CARDS (derivadas de initialState) ====
type LockedCardsMap = Record<string, { price: number; reason?: string }>;

const normalizeText = (s?: string) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const isIncludedStatus = (estatus?: string) => normalizeText(estatus).startsWith('inclui');
// ==== /LOCKED CARDS ====

type PackageDetail = { label: string; price: number };

type Item = {
  id: string;
  title: string;
  price: number;
  code: string;
  img: string;
  extras?: string[];
  breakdown?: PackageDetail[];
  individualPrice?: number;

  locked?: boolean;
  lockedReason?: string;
};

// =============== JWT: helpers minimalistas (en este archivo) ===============
let __embedToken: string | null = null;
let __renewTimer: number | null = null;
let __renewing = false;

// Lee params de la URL
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);

  const jwtParam = params.get('jwt') || '';

  // Si vienen recordId/loteId explícitos, úsalos (modo actual)
  let recordId = params.get('recordId') || params.get('oportunidadId') || '';
  let loteId = params.get('loteId') || '';

  // Si no vienen, intenta extraerlos del JWT (modo embed recomendado)
  if (jwtParam && (!recordId || !loteId)) {
    const p = decodeJwtPayload(jwtParam);
    if (p) {
      recordId = recordId || (p.recordId || p.prospectoId || '');
      loteId = loteId || (p.loteId || '');
    }
  }

  return { recordId, loteId, jwt: jwtParam };
};

function decodeJwtPayload(token: string): any | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getJwtTtlSeconds(token: string, fallbackSeconds = 15 * 60) {
  const p = decodeJwtPayload(token);
  if (!p || typeof p.exp !== 'number' || typeof p.iat !== 'number') return fallbackSeconds;
  const ttl = p.exp - p.iat;
  return Number.isFinite(ttl) && ttl > 0 ? ttl : fallbackSeconds;
}


async function fetchEmbedToken(): Promise<string> {
  const { recordId, loteId } = getUrlParams();
  const res = await fetch(`${API}/gpv/embed-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recordId, loteId })
  });
  const data = await res.json();
  if (!res.ok || !data?.token) {
    throw new Error(data?.message || 'No se pudo obtener token');
  }
  return data.token as string;
}

async function ensureToken(): Promise<string> {
  if (__embedToken) return __embedToken;

  // 1) Si el iframe recibió ?jwt=..., úsalo tal cual (no llames /embed-token)
  const { jwt } = getUrlParams();
  if (jwt) {
    setToken(jwt, getJwtTtlSeconds(jwt));
    return jwt;
  }

  // 2) Fallback: generar token vía backend (modo dev / legacy)
  const tok = await fetchEmbedToken();
  setToken(tok, getJwtTtlSeconds(tok));
  return tok;
}

function setToken(tok: string, ttlSeconds = 15 * 60) {
  __embedToken = tok;
  if (__renewTimer) window.clearTimeout(__renewTimer);
  // renueva 2 minutos antes de expirar
  __renewTimer = window.setTimeout(async () => {
    try {
      await renewToken();
    } catch {
      // si falla, lo dejaremos reintentar al próximo request con 401
    }
  }, Math.max(0, (ttlSeconds - 120) * 1000));
}

async function renewToken() {
  if (__renewing) return;
  __renewing = true;
  try {
    const tok = await fetchEmbedToken();
    setToken(tok);
  } finally {
    __renewing = false;
  }
}

// fetch autenticado con retry si caducó
async function authFetch(input: string, init: RequestInit = {}, doRetry = true): Promise<Response> {
  const token = await ensureToken();
  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const res = await fetch(input, { ...init, headers });
  if (res.status !== 401 || !doRetry) return res;

  // intentar renovar y reintentar una vez
  try {
    await renewToken();
    const h2 = new Headers(init.headers || {});
    h2.set('Authorization', `Bearer ${__embedToken}`);
    if (!h2.has('Content-Type')) h2.set('Content-Type', 'application/json');
    return await fetch(input, { ...init, headers: h2 });
  } catch {
    return res;
  }
}
// =============== /JWT helpers ===============

// ⬇️ initialState: tipos de mensaje recibido
type InitialStateUpgrade = {
  Id?: string;
  Name?: string;
  GPV_Atributo__c?: string;
  GPV_Tarjeta__c?: string;
  estatus?: string;
  packageId?: string;
  price?: number;
  packagePrice?: number;
  [k: string]: any;
};

const lockedCardsFromInitialState = (upgrades?: InitialStateUpgrade[] | null): LockedCardsMap => {
  const map: LockedCardsMap = {};
  (upgrades || []).forEach((u) => {
    const cardId = (u?.GPV_Tarjeta__c || '').trim();
    if (!cardId) return;
    if (!isIncludedStatus(u?.estatus)) return;

    map[cardId] = { price: 0, reason: 'Incluida por defecto en el paquete' };
  });
  return map;
};

type InitialStatePayload = {
  upgrades?: InitialStateUpgrade[];
  [k: string]: any;
};
type InitialStateMessage = {
  source?: string;
  event?: 'initialState';
  payload?: InitialStatePayload;
};
// ⬆️ initialState: tipos de mensaje recibido

type Catalog = {
  id: CatalogId;
  label: string;
  subs: string[];
  itemsBySub?: Record<string, Item[]>;
};

type CatalogId =
  | 'paquetes'
  | 'safety'
  | 'glasspro'
  | 'interior'
  | 'tecnologia'
  | 'estilo';

type LoteOutput = {
  porcentajeConstruccion: number | null;
  caracteristicas: string | null;
  valorTerreno: number | null;
  especificacion: string | null;
  anioProgramaEscrituracion: number | null;
  superficie: number | null;
  m2Construccion: number | null;
  idlistaPreciosLoteTipo: string | null;
  gerenteServicio: string | null;
  ctReal: boolean | null;
  fechaPromesaEntrega: string | null;
  cartaFiduciaria: boolean | null;
  qEscrituracion: unknown;
  valorTerrenoEscrituras: number | null;
  valorLoteTipo: number | null;
  valorBase: number | null;
  dtuReal: boolean | null;
};

type LoteResponse = {
  status: 'success' | 'error';
  data?: {
    lote?: { obtenerDatosLoteOutput?: LoteOutput };
    proyectoNombre?: string | null;
    proyectoId?: string | null;
    modeloNombre?: string | null;
    modeloId?: string | null;
    manzana?: string | null;
    banos?: string | null;
    recamaras?: string | null;
    fachada?: string | null;
    loteNombre?: string | null;
  };
  message?: string;
};

const CATALOG_ORDER: Record<CatalogId, number> = {
  paquetes: 0,
  safety: 1,
  glasspro: 2,
  interior: 3,
  tecnologia: 4,
  estilo: 5,
};

const CATALOG_LABEL: Record<CatalogId, string> = {
  paquetes: 'Paquetes',
  safety: 'Safety',
  glasspro: 'GlassPro',
  interior: 'Diseño de interior',
  tecnologia: 'Tecnología',
  estilo: 'Acabados',
};

const SUB_ALIASES: Record<CatalogId, Record<string, string>> = {
  glasspro: {
    CANCEL: 'Canceles',
    CANCELERIA: 'Canceles',
    CANCELES: 'Canceles',
    CORREDIZO: 'Canceles',
    ABATIBLE: 'Canceles',
    'PUERTA DE VIDRIO': 'Canceles',
    VENTANA: 'Canceles',
    BARANDAL: 'Barandal',
  },
  interior: {
    'BARRA DE COCINA': 'Barra de cocina',
    BARRA: 'Barra de cocina',
    CLOSET: 'Closet',
    COCINA: 'Cocina',
    CONEXION: 'Conexión',
    CONEXIÓN: 'Conexión',
    ELECTRODOMESTICO: 'Electrodomésticos',
    ELECTRODOMESTICOS: 'Electrodomésticos',
    ELECTRODOMÉSTICOS: 'Electrodomésticos',
  },
  tecnologia: {
    'A/C': 'Minisplit',
    MINISPLIT: 'Minisplit',
    CLIMA: 'Minisplit',
    DOMOTICA: 'Domótica',
    DOMÓTICA: 'Domótica',
    PANEL: 'Paneles',
    'PANEL SOLAR': 'Paneles',
    PANELES: 'Paneles',
  },
  estilo: {
    'BAÑO FLEX': 'Baño Flex',
    'BANO FLEX': 'Baño Flex',
    'CUARTO FLEX': 'Cuarto flex',
    'ESCALERA MARINA': 'Escalera marina',
    ESPEJO: 'Espejos',
    ESPEJOS: 'Espejos',
    GOTERON: 'Goterón',
    GOTERÓN: 'Goterón',
    'LINEA DE LLENADO': 'Línea de llenado',
    'LÍNEA DE LLENADO': 'Línea de llenado',
    'MURO VERDE': 'Muro verde',
    PASAMANOS: 'Pasamanos',
    'PASTO SINTETICO': 'Pasto sintético',
    'PASTO SINTÉTICO': 'Pasto sintético',
    PERGOLA: 'Pérgola',
    PÉRGOLA: 'Pérgola',
    PISO: 'Piso',
    'TANQUE ESTACIONARIO': 'Tanque estacionario',
    'TAPA BARANDAL': 'Tapa barandal',
    CUBIERTA: 'Piso',
    BARRA: 'Piso',
    PORCELANATO: 'Piso',
    MARMOL: 'Piso',
    MÁRMOL: 'Piso',
    GRANITO: 'Piso',
  },
  safety: {
    PORTON: 'Portón',
    PORTÓN: 'Portón',
    PROTECTOR: 'Protectores',
    PROTECTORES: 'Protectores',
    REJA: 'Rejas',
    REJAS: 'Rejas',
    MALLA: 'Mallas',
    MALLAS: 'Mallas',
    HERRERIA: 'Rejas',
    HERRERÍA: 'Rejas',
    CERCADO: 'Mallas',
    CERCA: 'Mallas',
    CERCAS: 'Mallas',
    CICLONICA: 'Mallas',
    CICLÓNICA: 'Mallas',
    PEATONAL: 'Portón',
    PUERTA: 'Portón',
  },
  paquetes: {},
};

const SUB_ORDER: Record<CatalogId, string[]> = {
  paquetes: ['Todos'],
  glasspro: ['Barandal', 'Canceles'],
  interior: ['Barra de cocina', 'Closet', 'Cocina', 'Conexión', 'Electrodomésticos'],
  tecnologia: ['Domótica', 'Minisplit', 'Paneles'],
  estilo: [
    'Baño Flex',
    'Cuarto flex',
    'Escalera marina',
    'Espejos',
    'Goterón',
    'Línea de llenado',
    'Muro verde',
    'Pasamanos',
    'Pasto sintético',
    'Pérgola',
    'Piso',
    'Tanque estacionario',
    'Tapa barandal',
  ],
  safety: ['Portón', 'Protectores', 'Rejas', 'Mallas'],
};

/* Igual que escenario 1: normaliza también "_" y "/" */
const normalizeToken = (s?: string | null) =>
  (s ?? '')
    .toString()
    .toUpperCase()
    .replace(/[_/]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

function normalizeSubcategory(
  catId: CatalogId,
  rawSub?: string | null,
  name?: string | null,
  desc?: string | null
): string | null {
  const map = SUB_ALIASES[catId] || {};
  const subRaw = normalizeToken(rawSub);
  const text = normalizeToken(`${name ?? ''} ${desc ?? ''}`);

  if (subRaw || text) {
    for (const k of Object.keys(map)) {
      if ((subRaw && subRaw.includes(k)) || (text && text.includes(k))) {
        return map[k];
      }
    }
  }

  switch (catId) {
    case 'glasspro':
      if (/\bBARANDAL(ES)?\b/.test(text)) return 'Barandal';
      if (/\b(CANCEL(ES)?|CANCELER[ÍI]A|CORREDIZ(O|A|AS|OS)|ABATIBLE(S)?|PUERTA(S)?\s+DE\s+VIDRIO|VENTANA(L|LES)?)\b/.test(text))
        return 'Canceles';
      break;
    case 'interior':
      if (/\bBARRA\s+DE\s+COCINA\b/.test(text) || /\bBARRA\b/.test(text)) return 'Barra de cocina';
      if (/\bCLOSET(S)?\b/.test(text)) return 'Closet';
      if (/\bCOCINA\b/.test(text)) return 'Cocina';
      if (/\bCONEXI[ÓO]N\b/.test(text)) return 'Conexión';
      if (/\bELECTRODOM[ÉE]STICO(S)?\b/.test(text)) return 'Electrodomésticos';
      break;
    case 'tecnologia':
      if (/\b(MINISPLIT|A\/C|AIRES?\s*ACONDICIONADO(S)?|CLIMA)\b/.test(text)) return 'Minisplit';
      if (/\bPANEL(ES)?\s+SOLAR(ES)?\b/.test(text) || /\bPANELES?\b/.test(text)) return 'Paneles';
      if (/\bDOM[ÓO]TICA|SMART\b/.test(text)) return 'Domótica';
      break;
    case 'estilo': {
      if (/\bBA[ÑN]O\s+FLEX\b/.test(text)) return 'Baño Flex';
      if (/\bCUARTO\s+FLEX\b/.test(text)) return 'Cuarto flex';
      if (/\bESCALERA\s+MARINA\b/.test(text)) return 'Escalera marina';
      if (/\bESPEJO(S)?\b/.test(text)) return 'Espejos';
      if (/\bGOTER[ÓO]N\b/.test(text)) return 'Goterón';
      if (/\bL[ÍI]NEA\s+DE\s+LLENADO\b/.test(text)) return 'Línea de llenado';
      if (/\bMURO\s+VERDE\b/.test(text)) return 'Muro verde';
      if (/\bPASAMANOS\b/.test(text)) return 'Pasamanos';
      if (/\bPASTO\s+SINT[ÉE]TICO\b/.test(text)) return 'Pasto sintético';
      if (/\bP[ÉE]RGOLA\b/.test(text)) return 'Pérgola';
      if (/\bPISO(S)?\b/.test(text) || /\bPORCELANATO\b/.test(text) || /\bLOSETA(S)?\b/.test(text) || /\bLAMINADO(S)?\b/.test(text))
        return 'Piso';
      if (/\bTANQUE\s+ESTACIONARIO\b/.test(text)) return 'Tanque estacionario';
      if (/\bTAPA\s+BARANDAL\b/.test(text)) return 'Tapa barandal';
      break;
    }
    case 'safety':
      if (/\b(PORT[ÓO]N(ES)?|PUERTA(S)?(\s+PEATONAL)?)\b/.test(text)) return 'Portón';
      if (/\bPROTECTOR(ES)?\b/.test(text)) return 'Protectores';
      if (/\b(REJA(S)?|HERRER[ÍI]A)\b/.test(text)) return 'Rejas';
      if (/\b(MALLA(S)?|CERCADO|CERCA(S)?|CICL[ÓO]NICA)\b/.test(text)) return 'Mallas';
      break;
  }
  return null;
}

function orderSubs(catId: CatalogId, subs: string[]): string[] {
  const priority = SUB_ORDER[catId] || [];
  const pIndex = new Map(priority.map((v, i) => [v, i]));
  return subs.slice().sort((a, b) => {
    const ia = pIndex.has(a) ? (pIndex.get(a) as number) : 999;
    const ib = pIndex.has(b) ? (pIndex.get(b) as number) : 999;
    return ia === ib ? a.localeCompare(b, 'es') : ia - ib;
  });
}

const currency = (n: number | null | undefined) =>
  typeof n === 'number' && isFinite(n) ? n.toLocaleString('es-MX') : '0';

const safeNum = (v: any) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const s = String(v ?? '').trim();
  if (!s) return 0;
  const cleaned = s.replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const norm = (s?: string | null) => (s ?? '').toString().toUpperCase();

const HIDDEN_BUCKET = '__NOSUB__';
const gt0 = (n: any) => typeof n === 'number' && isFinite(n) && n > 0;

const hasIntersection = (a?: Set<string>, b?: Set<string>) => {
  if (!a || !b) return false;
  let found = false;
  a.forEach((x) => {
    if (!found && b.has(x)) found = true;
  });
  return found;
};

/** Enviar eventos a Salesforce/LWC **/
function sendSfEvent(event: string, payload: any) {
  try {
    if (typeof window === 'undefined') return;
    if (window.parent === window) return;
    window.parent.postMessage(
      {
        source: 'GPV_Cotizador',
        event,
        payload,
      },
      '*'
    );
  } catch (e) {
    console.error('Error en sendSfEvent:', e);
  }
}

/** Clasificador de tarjetas → catálogo **/
function mapToCatalogId(
  grupo?: string | null,
  sub?: string | null,
  name?: string | null,
  desc?: string | null
): CatalogId {
  const g = norm(grupo);
  const s = norm(sub);
  const n = norm(name);
  const d = norm(desc);
  const text = `${n} ${d}`.replace(/[_/]/g, ' ');

  if (/\bMURO\s+VERDE\b/.test(text)) return 'estilo';
  if (/\bTAPA\s+BARANDAL\b/.test(text)) return 'estilo';

  const isPenInl = /\bPEN\b|\bINL\d*\b/.test(text);
  if (isPenInl) {
    if (/\b(CLOSET(S)?|VESTIDOR(ES)?|MUEBLE(S)?)\b/.test(text)) return 'interior';
    if (
      /\b(CUBIERTA(S)?|BARRA(S)?|GRANITO|M[ÁA]RMOL|QUAR?TZ|CUARZO|PISO(S)?|PORCELANATO|LOSETA(S)?|LAMINADO(S)?)\b/.test(text) ||
      /\b(BA[ÑN]O\s+FLEX|ESCALERA\s+MARINA|PASAMANOS|PASTO\s+SINT[ÉE]TICO|P[ÉE]RGOLA|TANQUE\s+ESTACIONARIO|ESPEJO(S)?|GOTER[ÓO]N|L[ÍI]NEA\s+DE\s+LLENADO|CUARTO\s+FLEX)\b/.test(text)
    )
      return 'estilo';
    if (
      /\b(CANCEL(ES)?|CANCELER[ÍI]A|CORREDIZ(O|A|AS|OS)|ABATIBLE(S)?|PUERTA(S)?\s+DE\s+VIDRIO|VENTANA(L|LES)?|BARANDAL(ES)?)\b/.test(
        text
      )
    )
      return 'glasspro';
    if (/\b(REJA(S)?|PROTECTOR(ES)?|HERRER[IÍ]A|MALLA(S)?|CERC(A|AS))\b/.test(text)) return 'safety';
    if (/\b(MINISPLIT|A\/C|CLIMA|AIRES?\s*ACONDICIONADO(S)?|PANEL(ES)?\s+SOLAR(ES)?|DOM[ÓO]TICA|SMART)\b/.test(text))
      return 'tecnologia';
  }

  if (/\b(CANCEL(ES)?|CANCELER[ÍI]A|CORREDIZ(O|A|AS|OS)|ABATIBLE(S)?|PUERTA(S)?\s+DE\s+VIDRIO|VENTANA(L|LES)?|BARANDAL(ES)?)\b/.test(text) || g.includes('CANCEL'))
    return 'glasspro';

  if (/\b(MINISPLIT|TON\b|A\/C|CLIMA|AIRES?\s*ACONDICIONADO(S)?|PANEL(ES)?\s+SOLAR(ES)?|SOLAR|DOM[ÓO]TICA|SMART)\b/.test(text) || g.includes('TECNO'))
    return 'tecnologia';

  if (/\b(REJA(S)?|PROTECTOR(ES)?|HERRER[IÍ]A|SEGURIDAD|CERC(A|AS)|MALLA(S)?)\b/.test(text)) return 'safety';

  if (/\bCOCINA(S)?\b/.test(text) || s.includes('COCINA')) return 'interior';
  if (/\b(CLOSET(S)?|VESTIDOR(ES)?|CARPINTER[IÍ]A|MUEBLE(S)?)\b/.test(text) || g.includes('CARP')) return 'interior';

  if (/\b(CUBIERTA(S)?|COCINA(S)?|ALACENA(S)?|BARRA(S)?|ACABADO(S)?|PISO(S)?|PORCELANATO|LOSETA(S)?|LAMINADO(S)?)\b/.test(text) ||
      s.includes('COCINA') ||
      /\b(BA[ÑN]O\s+FLEX|ESCALERA\s+MARINA|PASAMANOS|PASTO\s+SINT[ÉE]TICO|P[ÉE]RGOLA|TANQUE\s+ESTACIONARIO|ESPEJO(S)?|GOTER[ÓO]N|L[ÍI]NEA\s+DE\s+LLENADO|CUARTO\s+FLEX)\b/.test(text))
    return 'estilo';

  return 'safety';
}

/* UI: Tabs */
const TabBar: FC<{
  tabs: { id: CatalogId; label: string }[];
  value: CatalogId;
  onChange: (id: CatalogId) => void;
}> = ({ tabs, value, onChange }) => {
  return (
    <div className="gp-tabs-joined" role="tablist" aria-label="Categorías">
      {tabs.map((t, idx) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={value === t.id}
          className={`gp-tab-joined ${value === t.id ? 'is-active' : ''} ${idx === 0 ? 'is-first' : ''} ${idx === tabs.length - 1 ? 'is-last' : ''}`}
          onClick={() => onChange(t.id)}
          type="button"
        >
          {t.label}
        </button>
      ))}
      <div className="gp-tabs-underline" />
    </div>
  );
};

/* UI: Subcategorías */
const SubcategoryBar: FC<{
  subs: ReadonlyArray<string>;
  value: string;
  onChange: (s: string) => void;
  forceShowWhenSingle?: boolean;
}> = ({ subs, value, onChange, forceShowWhenSingle = false }) => {
  if (!subs?.length || (subs.length === 1 && subs[0] === 'Todos' && !forceShowWhenSingle)) return null;
  return (
    <div className="gp-subbar" role="tablist" aria-label="Subcategorías">
      {subs.map((s) => (
        <button
          key={s}
          role="tab"
          aria-selected={value === s}
          className={`gp-subchip ${value === s ? 'is-active' : ''}`}
          onClick={() => onChange(s)}
          type="button"
        >
          {s}
        </button>
      ))}
    </div>
  );
};

/* Tarjeta/Paquete */
const ItemCard: FC<{
  item: Item;
  selected: boolean;
  onToggle: () => void;
  onDetails?: () => void;
  showIndividualInfo?: boolean;
  showPackageRef?: boolean;
  isPackage?: boolean;
  onPreviewImage?: (src: string, alt?: string) => void;
}> = ({
  item,
  selected,
  onToggle,
  onDetails,
  showIndividualInfo,
  showPackageRef = false,
  isPackage = false,
  onPreviewImage, // ✅ NUEVO
}) => {
  const displayPrice =
    showIndividualInfo && gt0(item.individualPrice!) ? (item.individualPrice as number) : item.price;
  const hasExtras = (item.extras ?? []).length > 0;
  const locked = !!item.locked;

  return (
    <div className={`gp-item ${selected ? 'is-selected' : ''}`} style={{ position: 'relative' }}>
      {selected && <span className="gp-onlycheck" aria-hidden="true" />}
      <div className="gp-body">
        <div className="d-flex gap-4">
            <img
              className="gp-img"
              src={item.img || IMG_FALLBACK}
              alt={item.title}
              onClick={() => onPreviewImage?.(item.img || IMG_FALLBACK, item.title)}
              style={{ cursor: 'zoom-in' }}
            />
          <div className="flex-grow-1 d-flex flex-column">
            <div className="d-flex justify-content-between align-items-start">
              <button type="button" className="btn btn-link p-0 text-decoration-none gp-titlelink" onClick={onToggle}>
                <div className="fw-bold">{item.title}</div>
              </button>
              <button
                className="btn btn-sm btn-light"
                type="button"
                aria-label="Detalles"
                onClick={onDetails}
                disabled={!onDetails}
              >
                <i className="bi bi-chevron-right" aria-hidden="true"></i>
              </button>
            </div>

            <div className="text-gray-700">
              {showIndividualInfo && gt0(item.individualPrice!)
                ? <>Precio individual: ${(item.individualPrice as number).toLocaleString('es-MX')}</>
                : <>Precio: ${displayPrice.toLocaleString('es-MX')}</>}
            </div>
            <div className="gp-meta">Código: {item.code}</div>

            {showPackageRef && gt0(item.price) && (
              <div className="gp-meta">En paquete: ${item.price.toLocaleString('es-MX')}</div>
            )}

            {hasExtras && (
              <div className="mt-3">
                <div className="gp-meta fw-semibold">Incluye</div>
                <div className="small gp-chip">
                  {(item.extras ?? []).map((e, i, arr) => (
                    <span key={e}>
                      {i ? ' · ' : ''}
                      {i === arr.length - 1 ? <strong className="gp-strong">{e}</strong> : e}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!hasExtras && isPackage && (
              <div className="mt-3">
                <div className="gp-meta fw-semibold">Incluye</div>
                <div className="small gp-chip">
                  <span className="text-muted">Otros</span>
                </div>
              </div>
            )}

            <div className="mt-auto pt-3">
  {locked ? (
    <button
      className="btn btn-sm btn-light"
      type="button"
      disabled
      title={item.lockedReason || 'Incluido'}
    >
      Incluido · $0
    </button>
  ) : (
    <button
      className={`btn btn-sm ${selected ? 'btn-light-danger' : 'gp-btn-primary text-white'}`}
      onClick={onToggle}
    >
      {selected ? 'Quitar' : 'Agregar'}
    </button>
  )}
</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* Toasts */
type ToastMsg = { id: number; node: React.ReactNode };

const Toasts: FC<{ list: ToastMsg[]; onClose: (id: number) => void }> = ({ list, onClose }) => (
  <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 9999, maxWidth: 420 }}>
    {list.map((t) => (
      <div key={t.id} className="alert alert-warning d-flex align-items-start shadow-sm mb-2">
        <i className="bi bi-exclamation-triangle me-2 mt-1" />
        <div className="flex-grow-1">{t.node}</div>
        <button type="button" className="btn btn-sm btn-link" onClick={() => onClose(t.id)}>
          ×
        </button>
      </div>
    ))}
  </div>
);

// === NUEVO === Persistencia local / snapshot / watchdog
let initialStateCameFromHost = false;
const STORAGE_KEY = (oppId: string) => `gpv:lastState:${oppId || 'default'}`;

const saveSnapshot = (oportunidadId: string, upgrades: InitialStateUpgrade[]) => {
  try { localStorage.setItem(STORAGE_KEY(oportunidadId), JSON.stringify({ upgrades })); } catch {}
};
const readSnapshot = (oportunidadId: string): InitialStateUpgrade[] | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(oportunidadId));
    if (!raw) return null;
    const j = JSON.parse(raw);
    return Array.isArray(j?.upgrades) ? j.upgrades : null;
  } catch { return null; }
};

/** Helpers de metadatos para construir upgrades “ricos” */
const getPackageMetaBuilder = (packages: Item[]) => (pkgId: string) => {
  const p = packages.find((x) => x.id === pkgId);
  if (!p) return null;
  return {
    Id: p.id,
    Name: p.title,
    estatus: `PKG-${p.title}`,
    packageId: p.id,
    price: p.price ?? 0,
    packagePrice: p.price ?? 0,
  } as InitialStateUpgrade;
};

const getAddonMetaBuilder = (catalogCats: Catalog[]) => (cardId: string) => {
  for (const cat of catalogCats) {
    if (cat.id === 'paquetes') continue;
    const all = cat.itemsBySub?.['Todos'] || [];
    const it = all.find((x) => x.id === cardId);
    if (it) {
      return {
        Id: it.id,
        Name: it.title,
        GPV_Atributo__c: undefined,
        GPV_Tarjeta__c: it.id,
        estatus: `ADD-${cat.label}`,
        packageId: undefined,
        price: gt0(it.individualPrice) ? (it.individualPrice as number) : (it.price ?? 0),
        packagePrice: it.price ?? 0,
      } as InitialStateUpgrade;
    }
  }
  return null;
};

let getPackageMeta = (_: string) => null as InitialStateUpgrade | null;
let getAddonMeta = (_: string) => null as InitialStateUpgrade | null;

/** Construye upgrades “ricos” a partir de selección actual */
const snapshotUpgrades = (pkgs: Set<string>, addons: Record<CatalogId, Set<string>>): InitialStateUpgrade[] => {
  const out: InitialStateUpgrade[] = [];

  Array.from(pkgs).forEach((packageId) => {
    const meta = getPackageMeta(packageId);
    if (meta) out.push(meta);
    else out.push({ packageId });
  });

  (Object.keys(addons) as CatalogId[]).forEach((cat) => {
    if (cat === 'paquetes') return;
    addons[cat].forEach((cardId) => {
      const meta = getAddonMeta(cardId);
      if (meta) out.push(meta);
      else out.push({ GPV_Tarjeta__c: cardId });
    });
  });

  return out;
};

const persistAndNotify = (
  reason: string,
  oportunidadId: string,
  nextPkgs: Set<string>,
  nextAddons: Record<CatalogId, Set<string>>
) => {
  const upgrades = snapshotUpgrades(nextPkgs, nextAddons);
  saveSnapshot(oportunidadId, upgrades);
  // NOTE: temporalmente deshabilitado para evitar que el padre (Salesforce/LWC)
  // recargue el iframe en cada cambio. Rehabilítalo cuando el host ya no
  // dependa de este evento, o envíalo solo en una acción final (p.ej. Guardar).
  // sendSfEvent('state:changed', { upgrades, reason });
};
// === /NUEVO ===

const DashboardWrapper: FC = () => {
  const intl = useIntl();
  const title = intl.formatMessage({ id: 'MENU.DASHBOARD' });
  const [imgPreview, setImgPreview] = useState<{ src: string; alt?: string } | null>(null);
  const openImg = (src: string, alt?: string) => setImgPreview({ src, alt });
  const closeImg = () => setImgPreview(null);

  // Params desde la URL
  const params = new URLSearchParams(window.location.search);
  const oportunidadId = params.get('recordId') || params.get('oportunidadId') || '';
  const loteId        = params.get('loteId') || '';

  // Tarjetas incluidas por defecto (vienen en initialState con estatus "Incluído")
  const [lockedCards, setLockedCards] = useState<LockedCardsMap>({});
  const lockedCardsRef = useRef<LockedCardsMap>({});

  useEffect(() => {
    lockedCardsRef.current = lockedCards;
  }, [lockedCards]);

  const isLockedCard = (_loteId: string, cardId: string) =>
    !!lockedCardsRef.current?.[cardId];

  const lockedCardPrice = (_loteId: string, cardId: string, fallback: number) =>
    isLockedCard(_loteId, cardId) ? 0 : fallback;




  // Opcionales en la URL
  const proyectoIdParam = params.get('proyectoId') || '';
  const modeloIdParam   = params.get('modeloId') || '';

  // IDs “resueltos” (URL o respuesta de /gpv/lotes)
  const [resolvedProyectoId, setResolvedProyectoId] = useState<string>('');
  const [resolvedModeloId, setResolvedModeloId] = useState<string>('');
  useEffect(() => {
    setResolvedProyectoId(proyectoIdParam);
    setResolvedModeloId(modeloIdParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== Cargar PROSPECTO (con JWT) ======
  const [prosLoading, setProsLoading] = useState(true);
  const [prosError, setProsError] = useState<string | null>(null);
  const [pros, setPros] = useState<any | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setProsLoading(true);
      setProsError(null);
      try {
        const res = await authFetch(`${API}/gpv/prospectos`, {
          method: 'POST',
          body: JSON.stringify({ oportunidadId }),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.message || 'Error al consultar prospecto');
        const raw = payload?.data;
        const first = Array.isArray(raw) ? raw[0] : raw;
        if (!cancel) setPros(first ?? null);
      } catch (err: any) {
        if (!cancel) setProsError(err?.message ?? 'Error al consultar prospecto');
      } finally {
        if (!cancel) setProsLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [oportunidadId]);

  // ====== Cargar LOTE (con JWT) ======
  const [loteLoading, setLoteLoading] = useState(true);
  const [loteError, setLoteError] = useState<string | null>(null);
  const [lote, setLote] = useState<LoteResponse['data'] | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoteLoading(true);
      setLoteError(null);
      try {
        const res = await authFetch(`${API}/gpv/lotes`, {
          method: 'POST',
          body: JSON.stringify({ loteId }),
        });
        const payload: LoteResponse = await res.json();
        if (!res.ok || payload.status !== 'success') {
          throw new Error(payload?.message || 'Error al consultar lote');
        }
        if (!cancel) {
          setLote(payload.data ?? null);

          // Si no venían en la URL, tómalo del lote
          const projFromLote = payload.data?.proyectoId || '';
          const modFromLote  = payload.data?.modeloId || '';
          if (!proyectoIdParam && projFromLote) setResolvedProyectoId(projFromLote);
          if (!modeloIdParam   && modFromLote)  setResolvedModeloId(modFromLote);
        }
      } catch (err: any) {
        if (!cancel) setLoteError(err?.message ?? 'Error al consultar lote');
      } finally {
        if (!cancel) setLoteLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [loteId, proyectoIdParam, modeloIdParam]);

  type Tarjeta = any;
  type Paquete = any;
  type MediaRec = any;

  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [packages, setPackages] = useState<Item[]>([]);
  const [catalogCats, setCatalogCats] = useState<Catalog[]>([
    { id: 'paquetes', label: CATALOG_LABEL.paquetes, subs: ['Todos'] },
    { id: 'safety', label: CATALOG_LABEL.safety, subs: [], itemsBySub: {} },
    { id: 'glasspro', label: CATALOG_LABEL.glasspro, subs: [], itemsBySub: {} },
    { id: 'interior', label: CATALOG_LABEL.interior, subs: [], itemsBySub: {} },
    { id: 'tecnologia', label: CATALOG_LABEL.tecnologia, subs: [], itemsBySub: {} },
    { id: 'estilo', label: CATALOG_LABEL.estilo, subs: [], itemsBySub: {} },
  ]);

  const [pkgCardIndex, setPkgCardIndex] = useState<Record<string, Set<string>>>({});
  const [, setAllPkgCardIds] = useState<Set<string>>(new Set());
  const [fachadaMedia, setFachadaMedia] = useState<string | null>(null);

  // ====== Cargar Paquetes/Tarjetas/Media (con JWT) ======
  useEffect(() => {
    let cancel = false;

    // Espera a tener proyecto/modelo resueltos para pedir paquetes
    if (!resolvedProyectoId || !resolvedModeloId) {
      setLoadingCatalog(false);
      return () => { cancel = true; };
    }

    (async () => {
      setLoadingCatalog(true);
      setCatalogError(null);
      try {
        const [paqRes, tarjRes, mediaRes] = await Promise.all([
          authFetch(`${API}/gpv/paquetes`, {
            method: 'POST',
            body: JSON.stringify({ proyectoId: resolvedProyectoId, modeloId: resolvedModeloId }),
          }),
          authFetch(`${API}/gpv/tarjetas`, {
            method: 'POST',
            body: JSON.stringify({ loteId }),
          }),
          authFetch(`${API}/gpv/media`, {
            method: 'POST',
            body: JSON.stringify({ lote: loteId }),
          }),
        ]);

        const paqJson = await paqRes.json();
        const tarjJson = await tarjRes.json();
        const mediaJson = await mediaRes.json();

        if (!paqRes.ok || paqJson?.status !== 'success') throw new Error(paqJson?.message || 'Error paquetes');
        if (!tarjRes.ok || tarjJson?.status !== 'success') throw new Error(tarjJson?.message || 'Error tarjetas');
        if (!mediaRes.ok || mediaJson?.status !== 'success') throw new Error(mediaJson?.message || 'Error media');

        const paquetes: Paquete[] = paqJson.data || [];
        const tarjetas: Tarjeta[] = tarjJson.data || [];
        const media: MediaRec[] = mediaJson.data || [];

        let fachadaCandidate: string | null = null;
        for (let i = 0; i < Math.max(0, media.length); i++) {
          const m = media[i];
          const url = m?.GPV_URL_Archivo__c || '';
          const name = (m?.Name || m?.GPV_Nombre__c || '').toString().toUpperCase();
          const attrName = (m?.GPV_Atributo__r?.Name || '').toString().toUpperCase();
          const desc = (m?.GPV_Atributo__r?.GPV_Descripcion__c || '').toString().toUpperCase();
          if (!fachadaCandidate && (name.includes('FACHADA') || attrName.includes('FACHADA') || desc.includes('FACHADA'))) {
            fachadaCandidate = url || null;
          }
        }
        if (!fachadaCandidate) {
          const first = media.find((m: any) => (m?.GPV_URL_Archivo__c || '').startsWith('http'));
          fachadaCandidate = first?.GPV_URL_Archivo__c || null;
        }
        if (!cancel) setFachadaMedia(fachadaCandidate);

        const mByAttr: Record<string, string> = {};
        for (let i = 0; i < Math.max(0, media.length); i++) {
          const m = media[i];
          const attrId = m?.GPV_Atributo__r?.Id || null;
          const url = m?.GPV_URL_Archivo__c || null;
          if (attrId && url) mByAttr[attrId] = url;
        }
        const getAttrImage = (attrId?: string | null): string =>
          attrId && mByAttr[attrId] ? mByAttr[attrId] : IMG_FALLBACK;

        const tById: Record<string, any> = {};
        for (let i = 0; i < Math.max(0, tarjetas.length); i++) tById[tarjetas[i].Id] = tarjetas[i];

        const pkgItems: Item[] = [];
        const pkgIndexLocal: Record<string, Set<string>> = {};

        for (let pIdx = 0; pIdx < Math.max(0, paquetes.length); pIdx++) {
          const p = paquetes[pIdx];
          const rawCards = Array.isArray(p?.Tarjetas__r) ? p.Tarjetas__r : [];
          const cardIds: string[] = [];
          for (let r = 0; r < rawCards.length; r++) {
            const id = rawCards[r]?.GPV_Tarjeta__c || null;
            if (id) cardIds.push(id);
          }

          const lines: PackageDetail[] = [];
          let sum = 0;
          let firstImg = '';

          for (let c = 0; c < cardIds.length; c++) {
            const id = cardIds[c];
            const t = tById[id];
            if (!t) continue;

            const lp = t.ListasPreciosAtributosTarjeta__r?.[0] || {};
            const attr = t.GPV_Atributo__r || {};
            const label = attr?.Name || attr?.GPV_Descripcion__c || t.Name;
            const pricePkg = safeNum(lp.GPV_Precio_Paquete__c);
            const priceInd = safeNum(lp.GPV_Precio__c);
            const basePriceToUse = gt0(pricePkg) ? pricePkg : priceInd;

            const locked = isLockedCard(loteId, id);
            const priceToUse = locked ? 0 : lockedCardPrice(loteId, id, basePriceToUse);

            lines.push({ label, price: priceToUse });
            sum += priceToUse;

            const maybeImg = getAttrImage(attr?.Id);
            if (!firstImg && maybeImg) firstImg = maybeImg;
          }

          const extras: string[] = [];
          for (let i = 0; i < lines.length && i < 4; i++) extras.push(lines[i].label);

          pkgItems.push({
            id: p.Id,
            title: p.GPV_Nombre_Comercial__c || p.Name,
            price: sum,
            code: p.Name,
            img: firstImg || IMG_FALLBACK,
            extras,
            breakdown: lines.concat([{ label: 'TOTAL', price: sum }]),
          });

          pkgIndexLocal[p.Id] = new Set(cardIds.filter((id) => !!tById[id]));
        }

        const union = new Set<string>();
        Object.values(pkgIndexLocal).forEach((s) => s.forEach((id) => union.add(id)));

        type Seed = { label: string; subsSet: Set<string>; items: Record<string, Item[]> };
        const seedCats: Record<CatalogId, Seed> = {
          paquetes: { label: CATALOG_LABEL.paquetes, subsSet: new Set(), items: {} },
          safety: { label: CATALOG_LABEL.safety, subsSet: new Set(), items: {} },
          glasspro: { label: CATALOG_LABEL.glasspro, subsSet: new Set(), items: {} },
          interior: { label: CATALOG_LABEL.interior, subsSet: new Set(), items: {} },
          tecnologia: { label: CATALOG_LABEL.tecnologia, subsSet: new Set(), items: {} },
          estilo: { label: CATALOG_LABEL.estilo, subsSet: new Set(), items: {} },
        };

        for (let i = 0; i < Math.max(0, tarjetas.length); i++) {
          const t = tarjetas[i];
          const lp = t.ListasPreciosAtributosTarjeta__r?.[0] || {};
          const attr = t.GPV_Atributo__r || {};
          const groupId = mapToCatalogId(
            attr?.GPV_Grupo__c,
            attr?.GPV_Subgrupo__c,
            attr?.Name,
            attr?.GPV_Descripcion__c
          );

          const friendlySub = normalizeSubcategory(
            groupId,
            attr?.GPV_Subgrupo__c,
            attr?.Name,
            attr?.GPV_Descripcion__c
          );
          const subKey = friendlySub ? friendlySub : HIDDEN_BUCKET;

          const imgUrl = getAttrImage(attr?.Id);

          const locked = isLockedCard(loteId, t.Id);

const item: Item = {
  id: t.Id,
  title: attr?.Name || attr?.GPV_Descripcion__c || t.Name,
  code: t.Name,
  img: imgUrl,

  price: locked ? 0 : safeNum(lp.GPV_Precio_Paquete__c),
  individualPrice: locked ? 0 : safeNum(lp.GPV_Precio__c),

  locked,
  lockedReason: locked
    ? (lockedCardsRef.current?.[t.Id]?.reason || 'Incluida por defecto')
    : undefined,
};

          if (!seedCats[groupId].items[subKey]) seedCats[groupId].items[subKey] = [];
          if (subKey !== HIDDEN_BUCKET) seedCats[groupId].subsSet.add(subKey);
          seedCats[groupId].items[subKey].push(item);
        }

        const orderAlpha = (a: string, b: string) => a.localeCompare(b, 'es');

        let cats: Catalog[] = (Object.keys(seedCats) as CatalogId[]).map((cid) => {
          const c = seedCats[cid];

          const visibleSubs = orderSubs(
            cid,
            Array.from(c.subsSet).filter((s) => s && s.toUpperCase() !== 'TODOS')
          );
          const subs = visibleSubs.length ? ['Todos', ...visibleSubs] : ['Todos'];

          const itemsBySub: Record<string, Item[]> = {};
          const subKeys = Object.keys(c.items)
            .filter((k) => k !== HIDDEN_BUCKET)
            .sort(orderAlpha);

          for (const key of subKeys) {
            const list = (c.items[key] || []).slice().sort((a, b) => a.title.localeCompare(b.title, 'es'));
            itemsBySub[key] = list;
          }

          const all: Item[] = [];
          for (const key of Object.keys(c.items)) {
            const list = c.items[key] || [];
            for (let i = 0; i < list.length; i++) all.push(list[i]);
          }
          itemsBySub['Todos'] = all.sort((a, b) => a.title.localeCompare(b.title, 'es'));

          return { id: cid, label: CATALOG_LABEL[cid], subs, itemsBySub };
        });

        cats = cats
          .filter((c) => c.id === 'paquetes' || (c.itemsBySub?.Todos?.length ?? 0) > 0)
          .sort((a, b) => CATALOG_ORDER[a.id] - CATALOG_ORDER[b.id]);

        if (!cancel) {
          setPackages(pkgItems);
          setPkgCardIndex(pkgIndexLocal);
          setAllPkgCardIds(union);
          setCatalogCats(cats);

          // Resolutores de metadatos (snapshot rico)
          getPackageMeta = getPackageMetaBuilder(pkgItems);
          getAddonMeta = getAddonMetaBuilder(cats);
        }
      } catch (err: any) {
        if (!cancel) setCatalogError(err?.message || 'Error al cargar catálogo');
      } finally {
        if (!cancel) setLoadingCatalog(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loteId, resolvedProyectoId, resolvedModeloId]);

  const [selectedPackageIds, setSelectedPackageIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<CatalogId>('paquetes');
  const [activeSub, setActiveSub] = useState<string>('Todos');

  useEffect(() => {
    if (!catalogCats.find((c) => c.id === activeTab)) {
      const first = catalogCats[0]?.id || 'paquetes';
      setActiveTab(first as CatalogId);
    }
  }, [catalogCats, activeTab]);

  useEffect(() => {
    const cat = catalogCats.find((c) => c.id === activeTab);
    setActiveSub(cat && cat.subs && cat.subs.length ? cat.subs[0] : 'Todos');
  }, [activeTab, catalogCats]);

  const [selectedAddons, setSelectedAddons] = useState<Record<CatalogId, Set<string>>>({
    paquetes: new Set<string>(),
    safety: new Set<string>(),
    glasspro: new Set<string>(),
    interior: new Set<string>(),
    tecnologia: new Set<string>(),
    estilo: new Set<string>(),
  });

  /* TOASTS */
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const addToastNode = (node: React.ReactNode) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((lst) => [...lst, { id, node }]);
    window.setTimeout(() => {
      setToasts((lst) => lst.filter((t) => t.id !== id));
    }, 4500);
  };
  const closeToast = (id: number) => setToasts((lst) => lst.filter((t) => t.id !== id));

  const getPkgCards = (pkgId: string): Set<string> => pkgCardIndex[pkgId] || new Set<string>();

  /** Buscar metadatos de tarjeta (para el mensaje) */
  const lookupCardMeta = (cardId: string): { code: string; label: string } => {
    for (const cat of catalogCats) {
      const bySub = cat.itemsBySub || {};
      for (const k of Object.keys(bySub)) {
        const arr = bySub[k] || [];
        const found = arr.find((it) => it.id === cardId);
        if (found) return { code: found.code, label: found.title };
      }
    }
    return { code: cardId, label: '—' };
  };

  /** Qué paquetes seleccionados incluyen una tarjeta concreta */
  const packagesContainingCard = (cardId: string): Item[] => {
    const res: Item[] = [];
    for (const pid of Array.from(selectedPackageIds)) {
      const set = getPkgCards(pid);
      if (set.has(cardId)) {
        const p = packages.find((x) => x.id === pid);
        if (p) res.push(p);
      }
    }
    return res;
  };

  /** Mensaje rico de conflicto paquete↔paquete */
  const showPackageConflictToast = (attemptPkg: Item, keptPkg: Item, conflicts: Array<{ code: string; label: string }>) => {
    addToastNode(
      <div>
        <div className="fw-semibold mb-1">No se pudo agregar “{attemptPkg.title}”.</div>
        <div className="mb-2">
          Entra en conflicto con el paquete <strong>“{keptPkg.title}”</strong> por las siguientes tarjetas:
        </div>
        <ul className="mb-0">
          {conflicts.map((c) => (
            <li key={c.code}>
              <code>{c.code}</code> — {c.label}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  /** Mensaje rico de conflicto adicional↔paquete */
  const showAddonConflictToast = (addon: Item, pkgs: Item[]) => {
    addToastNode(
      <div>
        <div className="fw-semibold mb-1">No se pudo agregar el adicional “{addon.title}”.</div>
        <div className="mb-2">Esta tarjeta ya viene incluida en:</div>
        <ul className="mb-0">
          {pkgs.map((p) => (
            <li key={p.id}><strong>{p.title}</strong> (<code>{p.code}</code>)</li>
          ))}
        </ul>
      </div>
    );
  };

  /** Toggle paquete */
  const togglePackage = (pkgId: string) => {
    setSelectedPackageIds((prev) => {
      const wasSelected = prev.has(pkgId);

      if (wasSelected) {
        const next = new Set(prev);
        next.delete(pkgId);
        sendSfEvent('packageRemoved', { packageId: pkgId });
        persistAndNotify('packageRemoved', oportunidadId, next, selectedAddons);
        return next;
      }

      const thisCards = getPkgCards(pkgId);

      // conflictos con adicionales ya seleccionados
      const conflictingAddons: Array<{ id: string; title: string }> = [];
      for (const cat of Object.keys(selectedAddons) as CatalogId[]) {
        if (cat === 'paquetes') continue;
        for (const addonId of Array.from(selectedAddons[cat])) {
          if (thisCards.has(addonId)) {
            const c = catalogCats.find((cc) => cc.id === cat);
            const all: Item[] = c?.itemsBySub?.['Todos'] || [];
            const addon = all.find((x) => x.id === addonId);
            if (addon) conflictingAddons.push({ id: addon.id, title: addon.title });
          }
        }
      }

      if (conflictingAddons.length) {
        const pkg = packages.find((p) => p.id === pkgId)!;
        addToastNode(
          <div>
            <div className="fw-semibold mb-1">No se pudo agregar “{pkg.title}”.</div>
            <div className="mb-2">Este paquete ya incluye los siguientes adicionales seleccionados:</div>
            <ul className="mb-0">
              {conflictingAddons.map((a) => (
                <li key={a.id}><code>{a.id}</code> — {a.title}</li>
              ))}
            </ul>
          </div>
        );
        return prev;
      }

      // Conflictos paquete↔paquete
      let conflictingPkgId: string | null = null;
      for (const pid of Array.from(prev)) {
        const otherCards = getPkgCards(pid);
        if (hasIntersection(thisCards, otherCards)) {
          conflictingPkgId = pid;
          break;
        }
      }

      if (conflictingPkgId) {
        const otherCards = getPkgCards(conflictingPkgId);
        const conflicts: Array<{ code: string; label: string }> = [];
        thisCards.forEach((card) => {
          if (otherCards.has(card)) conflicts.push(lookupCardMeta(card));
        });

        const attempted = packages.find((p) => p.id === pkgId)!;
        const kept = packages.find((p) => p.id === conflictingPkgId)!;
        showPackageConflictToast(attempted, kept, conflicts);
        return prev;
      }

      // ✅ agrega paquete
      const next = new Set(prev);
      next.add(pkgId);
      const pkg = packages.find((p) => p.id === pkgId);
      if (pkg) sendSfEvent('packageAdded', { id: pkg.id, name: pkg.title });
      persistAndNotify('packageAdded', oportunidadId, next, selectedAddons);
      return next;
    });
  };

  /** Toggle adicional (bloquea si ya viene en un paquete seleccionado) */
  const toggleAddon = (catId: CatalogId, itemId: string) => {
    setSelectedAddons((prev) => {
      if (isLockedCard(loteId, itemId)) {
        return prev; // no se puede agregar ni quitar
      }

      const next: Record<CatalogId, Set<string>> = {
        paquetes: new Set(prev.paquetes),
        safety: new Set(prev.safety),
        glasspro: new Set(prev.glasspro),
        interior: new Set(prev.interior),
        tecnologia: new Set(prev.tecnologia),
        estilo: new Set(prev.estilo),
      };
      const bucket = next[catId];
      const wasSelected = bucket.has(itemId);

      if (wasSelected) {
        bucket.delete(itemId);
        sendSfEvent('upgradeRemoved', { id: itemId });
        persistAndNotify('upgradeRemoved', oportunidadId, selectedPackageIds, next);
        return next;
      }

      const pkgsWithCard = packagesContainingCard(itemId);
      if (pkgsWithCard.length) {
        const cat = catalogCats.find((c) => c.id === catId);
        const all: Item[] = cat?.itemsBySub?.['Todos'] || [];
        const addon = all.find((x) => x.id === itemId) || { id: itemId, title: itemId, code: itemId, img: '', price: 0 } as Item;
        showAddonConflictToast(addon, pkgsWithCard);
        return prev;
      }

      bucket.add(itemId);

      const cat = catalogCats.find((c) => c.id === catId);
      if (cat?.itemsBySub) {
        const all: Item[] = cat.itemsBySub['Todos'] || [];
        const it = all.find((x) => x.id === itemId);
        if (it) sendSfEvent('upgradeAdded', { id: it.id, name: it.title });
      }
      persistAndNotify('upgradeAdded', oportunidadId, selectedPackageIds, next);

      return next;
    });
  };

  const selectedPackagesList = useMemo(() => {
    const list: Array<Item & { qty: number }> = [];
    for (let i = 0; i < packages.length; i++) {
      const p = packages[i];
      if (selectedPackageIds.has(p.id)) list.push({ ...p, qty: 1 });
    }
    return list;
  }, [packages, selectedPackageIds]);

  const addonsList: Array<Item & { qty: number; cat: CatalogId }> = useMemo(() => {
    const list: Array<Item & { qty: number; cat: CatalogId }> = [];
    for (let c = 0; c < catalogCats.length; c++) {
      const cat = catalogCats[c];
      if (cat.id === 'paquetes') continue;
      const ids = Array.from(selectedAddons[cat.id] ?? []);
      const all: Item[] = [];
      const subKeys = Object.keys(cat.itemsBySub || {});
      for (let k = 0; k < subKeys.length; k++) {
        const key = subKeys[k];
        const arr = cat.itemsBySub && cat.itemsBySub[key] ? cat.itemsBySub[key] : [];
        for (let j = 0; j < arr.length; j++) all.push(arr[j]);
      }
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        for (const it of all) {
          if (it.id === id) {
            list.push({ ...it, qty: 1, cat: cat.id });
            break;
          }
        }
      }
    }
    return list;
  }, [selectedAddons, catalogCats]);

  // Regla de precios: con ≥1 paquete → usar precio de paquete; sin paquetes → precio individual.
  const getEffectivePrice = (it: Item) => {
    const hasAnyPackage = selectedPackageIds && selectedPackageIds.size > 0;
    if (hasAnyPackage) {
      return gt0(it.price) ? it.price : (gt0(it.individualPrice) ? (it.individualPrice as number) : 0);
    }
    return gt0(it.individualPrice) ? (it.individualPrice as number) : it.price;
  };

  const viviendaPrice: number = useMemo(() => {
    const out: LoteOutput | undefined = lote?.lote?.obtenerDatosLoteOutput;
    const base = out?.valorBase;
    const tipo = out?.valorLoteTipo;
    const terr = out?.valorTerreno;
    const value =
      typeof base === 'number'
        ? base
        : typeof tipo === 'number'
        ? tipo
        : typeof terr === 'number'
        ? terr
        : undefined;
    return typeof value === 'number' && isFinite(value) ? value : 0;
  }, [lote]);

  const subtotalPaquete = selectedPackagesList.reduce((acc, it) => acc + it.price * it.qty, 0);
  const subtotalAddons = addonsList.reduce((acc, it) => acc + getEffectivePrice(it) * it.qty, 0);
  const total = viviendaPrice + subtotalPaquete + subtotalAddons;

  const [detailsItem, setDetailsItem] = useState<Item | null>(null);
  const openDetails = (it: Item) => setDetailsItem(it);
  const closeDetails = () => setDetailsItem(null);

  const fachadaUrl = useMemo(() => {
    const u = (lote?.fachada ?? '').toString().trim();
    if (u) return u;
    if (fachadaMedia) return fachadaMedia;
    return IMG_FALLBACK;
  }, [lote, fachadaMedia]);

  // initialState: estado temporal por si llega el mensaje antes del catálogo
  const [pendingInitialUpgrades, setPendingInitialUpgrades] = useState<InitialStateUpgrade[] | null>(null);

  const findCatForCardId = (cardId: string): CatalogId | null => {
    for (const cat of catalogCats) {
      const list = cat.itemsBySub?.['Todos'] || [];
      if (list.some((it) => it.id === cardId)) return cat.id;
    }
    return null;
  };

  const applyInitialUpgrades = (upgs: InitialStateUpgrade[]) => {
    if (!upgs?.length) return;

    // 1) Paquetes primero
    const pkgIds = new Set<string>();
    upgs.forEach((u) => {
      const pid = (u.packageId || '').trim();
      if (pid && packages.some((p) => p.id === pid)) pkgIds.add(pid);
    });
    pkgIds.forEach((pid) => {
      if (!selectedPackageIds.has(pid)) togglePackage(pid);
    });

    // 2) Adicionales (solo si no están cubiertos por paquetes)
    upgs.forEach((u) => {
      const cardId = (u.GPV_Tarjeta__c || u.GPV_Atributo__c || '').trim();
      if (!cardId) return;

      if (isLockedCard(loteId, cardId)) return;

      const pkgsWithCard = packagesContainingCard(cardId);
      if (pkgsWithCard.length) return;

      const catId = findCatForCardId(cardId);
      if (!catId || catId === 'paquetes') return;

      if (!selectedAddons[catId].has(cardId)) {
        toggleAddon(catId, cardId);
      }
    });
  };

  // Listener de postMessage y ACK
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const data = e.data as InitialStateMessage;
      if (!data || data.event !== 'initialState') return;

      initialStateCameFromHost = true;
      const ups = data.payload?.upgrades || [];
      setLockedCards(lockedCardsFromInitialState(ups));
      setPendingInitialUpgrades(ups);

      saveSnapshot(oportunidadId, ups);

      try {
        window.parent?.postMessage(
          { source: 'GPV_Cotizador', event: 'initialState:ack', receivedAt: Date.now() },
          '*'
        );
      } catch {}
    };

    try {
      window.parent?.postMessage({ source: 'GPV_Cotizador', event: 'iframe:ready' }, '*');
    } catch {}

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [oportunidadId]);

  
  // Si initialState llega DESPUÉS de cargar catálogo, “parcha” las tarjetas incluidas (precio $0 + deshabilitadas)
  useEffect(() => {
    if (loadingCatalog) return;
    const ids = Object.keys(lockedCards || {});
    if (!ids.length) return;

    const lockedSet = new Set(ids);

    // 1) Catalogo (para que el botón muestre "Incluido · $0")
    setCatalogCats((prev) =>
      prev.map((cat) => {
        const next: any = { ...cat };
        const nextItemsBySub: any = {};
        Object.keys(cat.itemsBySub || {}).forEach((sub) => {
          const list = cat.itemsBySub?.[sub] || [];
          nextItemsBySub[sub] = list.map((it: any) => {
            if (!lockedSet.has(it.id)) return it;
            return {
              ...it,
              locked: true,
              lockedReason: lockedCards[it.id]?.reason || it.lockedReason,
              price: 0,
              individualPrice: 0,
            };
          });
        });
        next.itemsBySub = nextItemsBySub;
        return next;
      })
    );

    // 2) Paquetes (modal detalle)
    //    Intentamos ajustar la línea por título (sin meter nuevos campos/ids al breakdown).
    const titleById: Record<string, string> = {};
    catalogCats.forEach((cat) => {
      Object.values(cat.itemsBySub || {}).forEach((list: any) => {
        (list || []).forEach((it: any) => {
          titleById[it.id] = it.title;
        });
      });
    });

    setPackages((prev) =>
      prev.map((p: any) => {
        if (!Array.isArray(p.breakdown)) return p;

        let sum = 0;
        const nextLines = p.breakdown.map((ln: any) => {
          if (!ln || !ln.label) return ln;
          if (ln.label === 'TOTAL') return ln;

          // Si la línea corresponde a una tarjeta bloqueada, forzamos $0
          const shouldZero = ids.some((id) => titleById[id] && titleById[id] === ln.label);
          const price = shouldZero ? 0 : Number(ln.price || 0);
          sum += price;
          return { ...ln, price };
        });

        const withTotal = nextLines.map((ln: any) =>
          ln?.label === 'TOTAL' ? { ...ln, price: sum } : ln
        );

        return { ...p, price: sum, breakdown: withTotal };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCatalog, lockedCards, catalogCats]);

// Aplica initialState cuando ya hay catálogo
  useEffect(() => {
    if (loadingCatalog) return;
    if (!pendingInitialUpgrades || !pendingInitialUpgrades.length) return;

    applyInitialUpgrades(pendingInitialUpgrades);
    setPendingInitialUpgrades(null);

    try {
      window.parent?.postMessage({ source: 'GPV_Cotizador', event: 'initialState:applied' }, '*');
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCatalog]);

  // Fallback: si el host NO manda initialState en ~4s, usa snapshot local
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (loadingCatalog) return;
    const t = window.setTimeout(() => {
      if (initialStateCameFromHost) return;
      const snap = readSnapshot(oportunidadId);
      if (snap && snap.length) {
        applyInitialUpgrades(snap);
        try {
          window.parent?.postMessage(
            { source: 'GPV_Cotizador', event: 'initialState:applied:fallback', reason: 'localStorage' },
            '*'
          );
        } catch {}
      }
    }, 4000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCatalog, oportunidadId]);

  const renderByTab = () => {
    if (activeTab === 'paquetes') {
      if (loadingCatalog) return <div className="alert alert-light mb-0">Cargando paquetes…</div>;
      if (catalogError) return <div className="alert alert-danger mb-0">{catalogError}</div>;
      if (!packages.length) return <div className="alert alert-light mb-0">No hay paquetes disponibles.</div>;

      return (
        <>
          <SubcategoryBar subs={['Todos']} value={'Todos'} onChange={() => {}} forceShowWhenSingle />
          <div className="row g-5 gp-grid">
            {packages.map((p) => (
              <div className="col-12 col-md-6" key={p.id}>
                <ItemCard
                  item={p}
                  selected={selectedPackageIds.has(p.id)}
                  onToggle={() => togglePackage(p.id)}
                  onDetails={p.breakdown ? () => openDetails(p) : undefined}
                  isPackage
                  onPreviewImage={openImg}
                />
              </div>
            ))}
          </div>
        </>
      );
    }

    const cat = catalogCats.find((c) => c.id === activeTab)!;
    let items: Item[] = [];
    if (activeSub === 'Todos') {
      items = cat.itemsBySub?.['Todos'] ?? [];
    } else {
      items = cat.itemsBySub?.[activeSub] ?? [];
    }

    if (loadingCatalog) return <div className="alert alert-light mb-0">Cargando adicionales…</div>;
    if (catalogError) return <div className="alert alert-danger mb-0">{catalogError}</div>;

    return (
      <>
        <SubcategoryBar
          subs={catalogCats.find((c) => c.id === activeTab)?.subs ?? []}
          value={activeSub}
          onChange={setActiveSub}
        />
        <div className="row g-5 gp-grid">
          {items.map((it) => (
            <div className="col-12 col-md-6" key={it.id}>
              <ItemCard
                item={it}
                selected={selectedAddons[activeTab].has(it.id)}
                onToggle={() => toggleAddon(activeTab, it.id)}
                /* Mostrar ambos precios cuando existan */
                showIndividualInfo={gt0(it.individualPrice)}
                showPackageRef={gt0(it.price)}
                onPreviewImage={openImg} 
              />
            </div>
          ))}
          {!items.length && <div className="alert alert-light mb-0">No hay elementos en “{activeSub}”.</div>}
        </div>
      </>
    );
  };

  const proyecto = lote?.proyectoNombre ?? '—';
  const modelo = lote?.modeloNombre ?? '—';
  const manzana = lote?.manzana ?? '—';
  const loteNombre = lote?.loteNombre ?? '—';
  const recamaras = lote?.recamaras ?? '—';
  const banos = lote?.banos ?? '—';

  const out = lote?.lote?.obtenerDatosLoteOutput;
  const superficie = out?.superficie ?? null;
  const m2Constr = out?.m2Construccion ?? null;

  const parseISODateAsLocal = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  };

  const fechaEntrega =
    out?.fechaPromesaEntrega
      ? parseISODateAsLocal(out.fechaPromesaEntrega).toLocaleDateString('es-MX')
      : '—';

  return (
    <>
      <style>{`
        #kt_toolbar #kt_page_title h1::before{
          content:'';
          display:inline-block;
          width:22px;height:22px;margin-right:6px;
          background:url(${toAbsoluteUrl('/media/logos/icon-cotizador.png')}) no-repeat center/contain;
          vertical-align:-3px;
        }
      `}</style>

      <PageTitle
       
      >
        {title}
      </PageTitle>

      <div className="gp-theme">
        <div className="row g-5 g-xl-8">
          <div className="col-12 col-xl-8">
            <div className="gp-card mb-6">
              <div className="gp-header">
                <img alt="Logo" className="h-30px logo" src={toAbsoluteUrl('/media/logos/icon-vivienda.png')} />
                <span>Vivienda</span>
              </div>

              <div className="gp-body">
                <div className="mb-6">
                  <div className="gp-section-title mb-3">
                    <img alt="Logo" className="h-30px logo" src={toAbsoluteUrl('/media/logos/icon-vivienda.png')} />
                    <span>Vivienda</span>
                  </div>

                  {loteLoading && <div className="alert alert-light mb-0">Cargando vivienda…</div>}
                  {!loteLoading && loteError && <div className="alert alert-danger mb-0">{loteError}</div>}

                  {!loteLoading && !loteError && (
                    <div className="gp-hero">
                      <section className="gp-hero-info">
                        <div className="text-gray-700">
                          <div className="fw-semibold">{proyecto}</div>
                          <div>Modelo {modelo}</div>

                          <div className="mt-2">
                            Precio: <span className="gp-strong">${currency(viviendaPrice)}</span>
                          </div>

                          <div className="gp-lm-row mt-20">
                            <div className="gp-lm-chip">
                              <i className="bi bi-square" aria-hidden="true"></i>
                              <span className="gp-lm-label">Lote:</span>
                              <span className="gp-lm-value">{loteNombre}</span>
                            </div>
                            <div className="gp-lm-chip">
                              <i className="bi bi-grid-3x3-gap" aria-hidden="true"></i>
                              <span className="gp-lm-label">Manzana:</span>
                              <span className="gp-lm-value">{manzana}</span>
                            </div>
                          </div>

                          <div className="gp-specs-grid mt-3">
                            <div className="gp-spec">
                              <i className="bi bi-door-open" aria-hidden="true"></i>
                              <span>{recamaras} Recámaras</span>
                            </div>
                            <div className="gp-spec">
                              <i className="bi bi-droplet-half" aria-hidden="true"></i>
                              <span>{banos} Baños</span>
                            </div>
                            <div className="gp-spec">
                              <i className="bi bi-rulers" aria-hidden="true"></i>
                              <span>{superficie ?? '—'} m² Terreno</span>
                            </div>
                            <div className="gp-spec">
                              <i className="bi bi-aspect-ratio" aria-hidden="true"></i>
                              <span>{m2Constr ?? '—'} m².</span>
                            </div>
                          </div>

                          <div className="gp-meta mt-3">
                            Promesa de entrega: <strong>{fechaEntrega}</strong>
                          </div>
                        </div>
                      </section>

                      <aside className="gp-hero-media">
                        <button
                          type="button"
                          className="btn p-0 border-0 bg-transparent"
                          onClick={() => openImg(fachadaUrl, 'Fachada de la vivienda')}
                          style={{ cursor: 'zoom-in' }}
                        >
                          <img
                            src={fachadaUrl}
                            alt="Fachada de la vivienda"
                            referrerPolicy="no-referrer"
                            loading="lazy"
                            style={{ display: 'block' }}
                          />
                        </button>
                      </aside>
                    </div>
                  )}
                </div>

                <div>
                  <div className="gp-section-title mb-1">
                    <img alt="Logo" className="h-30px logo" src={toAbsoluteUrl('/media/logos/icon-vivienda.png')} />
                    <span>Paquetes y Adicionales</span>
                  </div>

                  <div className="gp-meta mb-3">
                    * Marca la casilla para seleccionar el paquete o adicional deseado y agregarlo al carrito.
                  </div>

                  <TabBar
                    tabs={catalogCats.map(({ id, label }) => ({ id, label }))}
                    value={activeTab}
                    onChange={(id) => setActiveTab(id)}
                  />

                  {renderByTab()}
                </div>
              </div>
            </div>

            <div className="gp-card">
              <div className="gp-header">
                <img alt="Logo" className="h-30px logo" src={toAbsoluteUrl('/media/logos/icon-prospecto.png')} />
                <span>Información del Prospecto</span>
              </div>
              <div className="gp-body">
                {prosLoading && <div className="alert alert-light mb-0">Cargando prospecto…</div>}
                {!prosLoading && prosError && <div className="alert alert-danger mb-0">{prosError}</div>}

                {!prosLoading && !prosError && pros && (
                  <div className="row gy-4 text-gray-700">
                    <div className="col-md-6 col-lg-3">
                      <div className="fw-semibold">Prospecto:</div>
                      <div>{pros?.Account?.Name ?? '-'}</div>
                      <div className="fw-semibold mt-3">No. Cliente:</div>
                      <div>{pros?.Account?.Id ?? '-'}</div>
                      <div className="fw-semibold mt-3">Fecha de nacimiento:</div>
                      <div>{pros?.Account?.GPV_Fecha_Nacimiento__c ?? '-'}</div>
                    </div>
                    <div className="col-md-6 col-lg-3">
                      <div className="fw-semibold">CURP:</div>
                      <div>{pros?.Account?.GPV_CURP__c ?? '-'}</div>
                      <div className="fw-semibold mt-3">RFC:</div>
                      <div>{pros?.Account?.GPV_RFC__c ?? '-'}</div>
                    </div>
                    <div className="col-md-6 col-lg-3">
                      <div className="fw-semibold">Nacionalidad:</div>
                      <div>{pros?.Account?.GPV_Nacionalidad__c ?? '-'}</div>
                      <div className="fw-semibold mt-3">Teléfono / Celular:</div>
                      <div>{pros?.Account?.PersonMobilePhone ?? '-'}</div>
                      <div className="fw-semibold mt-3">WhatsApp:</div>
                      <div>{pros?.Account?.PersonMobilePhone ?? '-'}</div>
                    </div>
                    <div className="col-md-6 col-lg-3">
                      <div className="fw-semibold">NSS:</div>
                      <div>{pros?.Account?.GPV_NSS__c ?? '-'}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-4">
            <div className="gp-cart-sticky">
              <div className="gp-card gp-cart">
                <div className="gp-header">
                  <img alt="Logo" className="h-25px logo" src={toAbsoluteUrl('/media/logos/icon-carrito.png')} />
                  <span>Carrito</span>
                </div>
                <div className="gp-body">
                  {selectedPackagesList.length === 0 && addonsList.length === 0 && (
                    <div className="alert alert-light">Aún no agregas artículos.</div>
                  )}

                  {selectedPackagesList.map((pkg) => (
                    <div className="d-flex align-items-start mb-5" key={pkg.id}>
                      <img
                        className="rounded me-4"
                        src={pkg.img || IMG_FALLBACK}
                        alt={pkg.title}
                        width={56}
                        height={56}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between">
                          <div className="fw-bold">{pkg.title}</div>
                          <button type="button" className="btn btn-icon btn-sm btn-light-danger" onClick={() => togglePackage(pkg.id)}>
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </div>
                        <div className="gp-meta">
                          Precio: ${pkg.price.toLocaleString('es-MX')} · Código: {pkg.code}
                        </div>
                      </div>
                    </div>
                  ))}

                  {addonsList.map((it) => {
                    const eff = getEffectivePrice(it);
                    const showPkgRef = selectedPackageIds.size > 0 && gt0(it.price);
                    return (
                      <div className="d-flex align-items-start mb-5" key={it.id}>
                        <img
                          className="rounded me-4"
                          src={it.img || IMG_FALLBACK}
                          alt={it.title}
                          width={56}
                          height={56}
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between">
                            <div className="fw-bold">{it.title}</div>
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-light-danger"
                              onClick={() => toggleAddon((it as any).cat, it.id)}
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </div>
                          <div className="gp-meta">
                            Precio: ${eff.toLocaleString('es-MX')}
                            {showPkgRef && gt0(it.individualPrice) && (
                              <> · <span className="text-muted">Individual: {(it.individualPrice as number).toLocaleString('es-MX')}</span></>
                            )} · Código: {it.code}
                          </div>
                        </div>
                      </div>
                      
                    );
                  })}
                  <Modal show={!!imgPreview} onHide={closeImg} centered size="lg">
                    <Modal.Header className="d-flex align-items-center">
                      <Modal.Title>Vista previa</Modal.Title>

                      <button
                        type="button"
                        className="btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center ms-auto"
                        style={{ width: 34, height: 34 }}
                        onClick={closeImg}
                        aria-label="Cerrar"
                      >
                        <i className="bi bi-x" style={{ fontSize: 22, lineHeight: 1 }} />
                      </button>
                    </Modal.Header>

                    <Modal.Body className="p-0">
                      {imgPreview?.src ? (
                        <img
                          src={imgPreview.src}
                          alt={imgPreview.alt || 'Imagen'}
                          style={{ width: '100%', height: 'auto', display: 'block' }}
                        />
                      ) : null}
                    </Modal.Body>
                  </Modal>
                  <div className="gp-divider"></div>
                  <div className="gp-summary">
                    <div className="line">
                      <span>Vivienda:</span>
                      <span className="fw-bold">$ {currency(viviendaPrice)}</span>
                    </div>
                    <div className="line">
                      <span>Paquete:</span>
                      <span className="fw-bold">$ {currency(subtotalPaquete)}</span>
                    </div>
                    <div className="line">
                      <span>Adicionales (individual):</span>
                      <span className="fw-bold">$ {currency(subtotalAddons)}</span>
                    </div>
                    <div className="total">
                      <span>TOTAL:</span>
                      <span>$ {currency(total)}</span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <button className="btn gp-btn-primary w-100 d-inline-flex align-items-center justify-content-center gap-2 py-3" type="button">
                      <img
                        src={toAbsoluteUrl('/media/logos/icon-enviar-cotizacion.png')}
                        alt=""
                        width={20}
                        height={20}
                        className="align-middle"
                      />
                      <span className="fw-semibold text-white">Enviar Cotización (demo)</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {detailsItem && (
        <div
          className="modal fade show d-block gp-modal"
          role="dialog"
          aria-modal="true"
          style={{ background: 'rgba(0,0,0,.5)' }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{detailsItem.title} – Detalle</h5>
                <button type="button" className="btn-close" aria-label="Cerrar" onClick={closeDetails}></button>
              </div>

              <div className="modal-body">
                {detailsItem.breakdown?.length ? (
                  <>
                    <div className="table-responsive">
                      <table className="table gp-table-clean align-middle">
                        <thead>
                          <tr>
                            <th className="text-start gp-th-incluye">INCLUYE</th>
                            <th className="text-end">Precio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailsItem.breakdown
                            .filter((row) => !/^precio paquete/i.test(row.label))
                            .map((row, idx) => (
                              <tr key={idx} className={row.label.toUpperCase() === 'TOTAL' ? 'gp-row-total' : ''}>
                                <td>{row.label}</td>
                                <td className="text-end">${row.price.toLocaleString('es-MX')}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="gp-price-badge alert alert-primary d-flex justify-content-between mb-0">
                      <span className="fw-semibold">Precio Paquete</span>
                      <span className="fw-bold">${detailsItem.price.toLocaleString('es-MX')}</span>
                    </div>
                  </>
                ) : (
                  <div className="alert alert-light mb-0">Sin información de desglose.</div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-light" type="button" onClick={closeDetails}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toasts list={toasts} onClose={closeToast} />
    </>
  );
};


export { DashboardWrapper };
